import React, { useState, useEffect, useRef, useContext } from "react";
import Rangeslider from "./rangeSlider";
import MyCalendar from "components/calendar";
import ActiveStepIndicator from "./activeStepIndicator";
import { weekDays } from "utils/constants/onboardingStep";
import { MobXProviderContext } from "mobx-react";
import { toJS } from "mobx";
import { preventMinus, preventPasteNegative } from "utils/helpers/validation";
import {
  CalendarIcon,
  calendarIcon,
  CalendarWhiteIcon,
  dangerIcon,
  InfoButtonGray,
} from "assets/img";
import classNames from "classnames";
import { useEarliestDate } from "utils/hooks/useEarliestDate";

const Step3 = ({ disableButton }) => {
  const store = useContext(MobXProviderContext);
  const onboardingStore = toJS(store?.onboardingStore);
  const {
    selectedMealArray,
    enable_meal_delivery,
    recieving_days,
    isNotSureSelected,
    default_date,
    meal_allowance_value,
    meal_allowance_type,
    mealPlanSubsidy,
    enableOverpayment,
    dailySubsidyOverride,
  } = onboardingStore;

  const [overPayment, setEnableOverPayment] = useState(
    enableOverpayment || true
  );
  const [defaultSubsidy, setDefaultSubsidy] = useState(mealPlanSubsidy || 100);
  const [defaultDate, setDefaultDate] = useState(default_date || "");
  const [selectDateHover, setSelectDateHover] = useState(false);
  const [errors, setErrors] = useState({});
  const [mealDeliveryOption, setMealDeliveryOption] = useState(
    enable_meal_delivery || "every week"
  );
  const [mealRecievingDays, setMealRecievingDays] = useState(
    recieving_days || []
  );

  const isDayEnabled = (day) => mealRecievingDays?.includes(day);

  const [mealStartDate, setMealStartDate] = useState(
    isNotSureSelected
      ? "I am not sure yet"
      : default_date !== ""
      ? "select date"
      : ""
  );
  const [mealAllowanceType, setMealAllowanceType] = useState(
    // meal_allowance_type || ""
    "daily"
  );
  const [mealAllowanceValue, setMealAllowanceValue] = useState(
    meal_allowance_value || 0
  );
  const [showCalendar, setShowCalendar] = useState(
    mealStartDate !== "" &&
      mealStartDate?.toLowerCase() !== "i am not sure yet" &&
      enable_meal_delivery?.toLowerCase() === "every week"
  );
  const calendarRef = useRef(null);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const allDays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const [dailySubsidy, setDailySubsidy] = useState(() =>
    allDays.reduce((acc, day) => {
      acc[day] = "";
      return acc;
    }, {})
  );

  const currentMonthIndex = new Date().getMonth();
  const nextMonthIndex = (currentMonthIndex + 1) % 12;
  const nextNextMonthIndex = (currentMonthIndex + 2) % 12;

  const currentMonthName = monthNames[currentMonthIndex];
  const nextMonthName = monthNames[nextMonthIndex];
  const nextNextMonthName = monthNames[nextNextMonthIndex];

  const initialMealSelectedDates = [
    { [currentMonthName]: [] },
    { [nextMonthName]: [] },
    { [nextNextMonthName]: [] },
  ];

  const normalizeMealSelectedDates = (existingDates = []) => {
    const defaultMonths = [currentMonthName, nextMonthName, nextNextMonthName];

    return defaultMonths?.map((monthName) => {
      const found = existingDates.find(
        (entry) =>
          entry && Object.prototype.hasOwnProperty.call(entry, monthName)
      );
      return found || { [monthName]: [] };
    });
  };

  const [mealSelectedDates, setMealSelectedDates] = useState(() =>
    normalizeMealSelectedDates(
      onboardingStore.recieving_dates || initialMealSelectedDates
    )
  );

  let [
    mealSelectedDatesMonth1,
    mealSelectedDatesMonth2,
    mealSelectedDatesMonth3,
  ] = mealSelectedDates;

  let { earliestDate } = useEarliestDate();

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
    });
  }, []);

  useEffect(() => {
    if (mealAllowanceValue === 0 && mealAllowanceType === "daily") {
      handleMealAllowance(0);
    } else if (mealAllowanceValue === 0 && mealAllowanceType === "weekly") {
      handleMealAllowance(100);
    }
  }, [mealAllowanceType, mealAllowanceValue]);

  useEffect(() => {
    const isWeekly = mealDeliveryOption === "every week";
    const isOwnDates = mealDeliveryOption === "i will set my own dates";

    const allEmpty = mealSelectedDates?.every((month) => {
      return month && Object.values(month)[0]?.length === 0;
    });

    // NEW: check if all visible/selected days have a subsidy value
    const areAllDailySubsidiesFilled =
      mealAllowanceType?.toLowerCase() === "daily"
        ? mealRecievingDays.every(
            (day) =>
              dailySubsidyOverride?.[day]?.toString().trim() !== "" &&
              parseFloat(dailySubsidyOverride?.[day]) >= 0
          )
        : true;

    if (isWeekly) {
      if (
        mealRecievingDays.length !== 0 &&
        mealStartDate !== "" &&
        mealAllowanceType !== "" &&
        areAllDailySubsidiesFilled
      ) {
        disableButton(false);
      } else {
        disableButton(true);
      }
    } else if (isOwnDates) {
      if (!allEmpty && mealAllowanceType !== "") {
        disableButton(false);
      } else {
        disableButton(true);
      }
    }
  }, [
    mealDeliveryOption,
    mealSelectedDates,
    mealRecievingDays,
    mealAllowanceType,
    mealStartDate,
    dailySubsidyOverride, // add this dependency
    disableButton,
  ]);

  useEffect(() => {
    let isEveryWeekSelected = false;
    let selectedEarliestDate = earliestDate(mealSelectedDates);

    if (mealDeliveryOption?.toLowerCase() == "i will set my own dates") {
      isEveryWeekSelected = false;
    } else if (mealDeliveryOption?.toLowerCase() == "every week") {
      isEveryWeekSelected = true;
    }
    store.onboardingStore.handleMealSeriesAndSubsidiesData(
      isEveryWeekSelected ? mealRecievingDays : [],
      isEveryWeekSelected
        ? mealStartDate == "select date"
          ? defaultDate
          : mealStartDate
        : selectedEarliestDate,
      !isEveryWeekSelected ? mealSelectedDates : [],
      mealAllowanceType,
      mealAllowanceValue,
      mealDeliveryOption,
      overPayment,
      defaultSubsidy
    );
  }, [
    onboardingStore,
    mealRecievingDays,
    mealStartDate,
    mealSelectedDates,
    mealAllowanceType,
    mealAllowanceValue,
    mealDeliveryOption,
    earliestDate,
    defaultDate,
    overPayment,
    mealPlanSubsidy,
  ]);

  const onChangeDaily = (value, day) => {
    store.onboardingStore.setDailySubsidyOverride({ [day]: value });
    setErrors((prev) => ({ ...prev, [day]: false }));
  };

  const handleRecievingDaysClick = (value) => {
    if (mealRecievingDays.includes(value)) {
      setMealRecievingDays((prevDays) =>
        prevDays.filter((day) => day !== value)
      );
    } else {
      setMealRecievingDays((prevDays) => [...prevDays, value]);
    }
  };

  const handleMealAllowance = (value) => {
    const maxValue = mealAllowanceType?.toLowerCase() === "daily" ? 50 : 200;
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (value >= 0 && value <= maxValue) {
      if (regex.test(value)) {
        setMealAllowanceValue(value);
      } else {
        setMealAllowanceValue(value);
      }
    }
  };

  const handleMealAllowanceType = (value) => {
    setMealAllowanceType(value);
    if (value === "daily") {
      setMealAllowanceValue(20);
    } else if (value === "weekly") {
      setMealAllowanceValue(100);
    } else {
      setMealAllowanceValue(0);
    }
  };

  const handleMultipleDateChange = (calendarKey, newDate) => {
    const updatedMonthsArray = [...mealSelectedDates];

    const selectedMonthIndex = newDate?.[0]?.month?.number - 1;
    const selectedMonthName = monthNames[selectedMonthIndex];

    if (newDate?.length > 0) {
      updatedMonthsArray[calendarKey - 1] = {
        [selectedMonthName]: newDate.map(
          (date) => `${date.day}-${date.month.number}-${date.year}`
        ),
      };
    } else {
      const existingMonth =
        Object.keys(updatedMonthsArray[calendarKey - 1] || {})[0] ||
        monthNames[(new Date().getMonth() + calendarKey - 1) % 12];

      updatedMonthsArray[calendarKey - 1] = {
        [existingMonth]: [],
      };
    }

    setMealSelectedDates(normalizeMealSelectedDates(updatedMonthsArray));
  };

  const handleDateChange = (value) => {
    const parsedDate = new Date(value);
    setDefaultDate(
      parsedDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    );

    setMealAllowanceType("daily");
    setMealAllowanceValue(20);
  };

  const capitalizeFirstLetter = (string) => {
    return string?.charAt(0)?.toUpperCase() + string?.slice(1);
  };

  return (
    <div className="pb-4 mt-4">
      <ActiveStepIndicator step={3} />

      <div className="flex-col flex gap-6">
        <div className="expected-bag-text screen-xs:w-full-235 font-inter-semibold">
          How often do you want to enable {selectedMealArray?.meal_name}{" "}
          delivery?
        </div>

        <div className="mb-0.5 w-full screen-xs:w-full-215">
          <div className="screen-lg:inline-flex screen-md:inline-flex">
            <div
              className={`screen-lg:mr-3 screen-md:mr-3 mt-4 screen-lg:mt-0 screen-md:mt-0 px-4 py-3 hover:cursor-pointer text-slate-700 font-inter-medium text-sm leading-[14px] ${
                mealDeliveryOption === "every week"
                  ? "bg-indigo-500 text-white "
                  : "bg-slate-50 hover:bg-indigo-500 hover:text-white "
              } rounded-3xl justify-center gap-2 flex`}
              onClick={() => {
                setMealDeliveryOption("every week");
                if (defaultDate !== "") setShowCalendar(true);
                setMealRecievingDays([]);
                setMealStartDate("");
                setMealAllowanceType("daily");
                setDefaultDate("");
              }}
            >
              Every week
            </div>

            <div
              className={`screen-lg:mr-3 screen-md:mr-3 mt-4 screen-lg:mt-0 screen-md:mt-0 px-4 py-3 hover:cursor-pointer text-slate-700 font-inter-medium text-sm leading-[14px] ${
                mealDeliveryOption === "i will set my own dates"
                  ? "bg-indigo-500 text-white "
                  : "bg-slate-50 hover:bg-indigo-500 hover:text-white "
              } rounded-3xl justify-center gap-2 flex`}
              onClick={() => {
                setMealDeliveryOption("i will set my own dates");
                setShowCalendar(false);
                setMealAllowanceType("daily");
                setMealAllowanceValue(20);
                setMealSelectedDates([]);
                setDefaultDate("");
              }}
            >
              I will set my own dates
            </div>
          </div>
        </div>

        {mealDeliveryOption === "every week" && (
          <>
            <div className="expected-bag-text screen-xs:w-full-235 font-inter-semibold">
              What days?
            </div>

            <div className="mb-0.5 w-full screen-xs:w-full-215">
              <div className="screen-lg:inline-flex screen-md:inline-flex">
                {weekDays.map((value, index) => (
                  <div
                    key={index}
                    className={`screen-lg:mr-3 screen-md:mr-3 mt-4 screen-lg:mt-0 screen-md:mt-0 px-4 py-3 hover:cursor-pointer text-slate-700 font-inter-medium text-sm leading-[14px] capitalize ${
                      mealRecievingDays.some((day) => day === value)
                        ? "bg-indigo-500 text-white "
                        : "bg-slate-50 hover:bg-indigo-500 hover:text-white "
                    } rounded-3xl justify-center gap-2 flex`}
                    onClick={() => handleRecievingDaysClick(value)}
                  >
                    {value}
                  </div>
                ))}
              </div>
            </div>

            <div className="expected-bag-text screen-xs:w-full-235 font-inter-semibold">
              When would you like to start?
            </div>

            <div className="mb-0.5 w-full screen-xs:w-full-215">
              <div className="screen-lg:inline-flex screen-md:inline-flex flex">
                <div className="mb-0.5 w-full screen-xs:w-full-215">
                  <div className="screen-lg:inline-flex screen-md:inline-flex">
                    <div
                      className={classNames(
                        "hidden screen-lg:flex screen-md:flex screen-lg:w-46 screen-md:w-46 h-12 screen-lg:mr-3 screen-md:mr-3 mt-4 px-4 py-3 rounded-3xl items-center justify-between cursor-pointer gap-2",
                        {
                          "bg-indigo-500 text-white ":
                            mealStartDate == "select date",
                          "bg-slate-50 text-slate-700 hover:bg-indigo-500 hover:text-white ":
                            mealStartDate !== "select date",
                        }
                      )}
                      onClick={() => {
                        setMealStartDate("select date");
                        setShowCalendar(true);
                      }}
                      onMouseEnter={() => setSelectDateHover(true)}
                      onMouseLeave={() => setSelectDateHover(false)}
                    >
                      Select date
                      <span>
                        <img
                          className="w-[20px] h-[20px] "
                          src={
                            mealStartDate == "select date" || selectDateHover
                              ? CalendarWhiteIcon
                              : CalendarIcon
                          }
                          alt="CalendarIcon"
                        />
                      </span>
                    </div>

                    <div
                      className={classNames(
                        "screen-lg:hidden screen-md:hidden flex h-12 mt-4 px-4 py-3 rounded-xl items-center justify-between cursor-pointer gap-2 bg-slate-50 text-slate-700"
                      )}
                      onClick={() => {
                        setMealStartDate("select date");
                        setShowCalendar(true);
                      }}
                      onMouseEnter={() => setSelectDateHover(true)}
                      onMouseLeave={() => setSelectDateHover(false)}
                    >
                      {defaultDate !== "" &&
                      defaultDate?.toLowerCase() !== "invalid date"
                        ? defaultDate
                        : "Select date"}
                      <span>
                        <img
                          className="w-[20px] h-[20px] "
                          src={calendarIcon}
                          alt="CalendarIcon"
                        />
                      </span>
                    </div>

                    <div
                      className={`screen-lg:w-40 screen-md:w-40 h-12 screen-lg:mr-3 screen-md:mr-3 mt-4 px-4 py-3 hover:cursor-pointer text-indigo-500 font-inter-medium text-sm leading-[14px] ${
                        mealStartDate?.toLowerCase() === "i am not sure yet"
                          ? "bg-indigo-500 text-white "
                          : "bg-slate-50 hover:bg-indigo-500 hover:text-white "
                      } screen-lg:rounded-3xl screen-md:rounded-3xl rounded-xl items-center justify-center gap-2 flex`}
                      onClick={() => {
                        setShowCalendar(false);
                        setMealStartDate("I am not sure yet");
                        setDefaultDate("");
                      }}
                    >
                      I am not sure yet
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {showCalendar && (
          <div className="w-44" ref={calendarRef}>
            <MyCalendar
              highlightToday={false}
              disableMonthYearPicker={true}
              isMultiple={false}
              showButtons={false}
              handleDatePicker={handleDateChange}
              value={1}
              selectedValue={defaultDate}
            />
          </div>
        )}

        {mealDeliveryOption === "i will set my own dates" && (
          <div className="flex inline-flex gap-5 overflow-x-auto z-0">
            <MyCalendar
              highlightToday={false}
              disableMonthYearPicker={true}
              isMultiple={true}
              showButtons={false}
              handleMultipleDates={(dates) =>
                handleMultipleDateChange(1, dates)
              }
              value={1}
              multipleValues={mealSelectedDatesMonth1}
              isOwnDates={true}
            />

            <MyCalendar
              highlightToday={false}
              disableMonthYearPicker={true}
              isMultiple={true}
              showButtons={false}
              handleMultipleDates={(dates) =>
                handleMultipleDateChange(2, dates)
              }
              value={2}
              multipleValues={mealSelectedDatesMonth2}
              isOwnDates={true}
            />

            <MyCalendar
              highlightToday={false}
              disableMonthYearPicker={true}
              isMultiple={true}
              showButtons={false}
              handleMultipleDates={(dates) =>
                handleMultipleDateChange(3, dates)
              }
              value={3}
              multipleValues={mealSelectedDatesMonth3}
              isOwnDates={true}
            />
          </div>
        )}

        {mealDeliveryOption === "every week" && (
          <>
            <div className="expected-bag-text screen-xs:w-full-235 font-inter-semibold">
              Meal Plan Subsidy
            </div>

            <div className="mb-0.5 w-full screen-xs:w-full-215">
              <div className="screen-lg:inline-flex screen-md:inline-flex">
                <div
                  className={`screen-lg:mr-3 screen-md:mr-3 mt-4 screen-lg:mt-0 screen-md:mt-0 px-4 py-3 hover:cursor-pointer text-slate-700 font-inter-medium text-sm leading-[14px] ${
                    mealAllowanceType === "daily"
                      ? "bg-indigo-500 text-white "
                      : "bg-slate-50 hover:bg-indigo-500 hover:text-white "
                  } rounded-3xl justify-center gap-2 flex`}
                  onClick={() => handleMealAllowanceType("daily")}
                >
                  Per Meal
                </div>

                <div
                  className={`screen-lg:mr-3 screen-md:mr-3 mt-4 screen-lg:mt-0 screen-md:mt-0 px-4 py-3 hover:cursor-pointer text-slate-700 font-inter-medium text-sm leading-[14px] ${
                    mealAllowanceType === "weekly"
                      ? "bg-indigo-500 text-white "
                      : "bg-slate-50 hover:bg-indigo-500 hover:text-white "
                  } rounded-3xl justify-center gap-2 flex`}
                  onClick={() => handleMealAllowanceType("weekly")}
                >
                  Weekly
                </div>
              </div>
            </div>
          </>
        )}

        {mealDeliveryOption != "i will set my own dates" && (
          <div className="max-w-[705px] self-start">
            {mealAllowanceType?.toLowerCase() == "daily" ? (
              <small className="text-[#566985] w-1/3 font-inter-regular text-[14px]">
                Setting a per meal subsidy allows users to use this amount for
                each meal. To exclude a specific day, enter 0 for that day. You
                can also assign custom subsidies to individual users, but for
                now, start by setting the default per meal subsidy.
              </small>
            ) : (
              <small className="text-[#566985] w-1/2 font-inter-regular text-[14px]">
                Users can spend their weekly subsidy across multiple days and it
                may run out before the week ends. Weekly subsidies reset every
                Monday at 12 AM.
              </small>
            )}
          </div>
        )}

        {mealAllowanceType != "" &&
          mealAllowanceType?.toLowerCase() == "daily" &&
          mealDeliveryOption != "i will set my own dates" && (
            <div className="d-col bg-[#F7F9FF] max-w-[705px] p-[12px] rounded-lg">
              <div className="grid grid-cols-3 screen-lg:grid-cols-7 gap-4 py-[12px] px-[10px]">
                {allDays.map((day) => {
                  const disabled = !isDayEnabled(day);

                  return (
                    <div key={day} className="text-center rounded">
                      <span className="text-sm font-inter-medium text-gray-700 capitalize">
                        {day?.slice(0, 3)}
                      </span>

                      <div
                        className={classNames(
                          "d-row px-[14px] py-[7px] mt-[7px] rounded-xl items-center",
                          disabled
                            ? "bg-[#E4E5EA]"
                            : errors?.[day]
                            ? "bg-white border border-red-500"
                            : "bg-white"
                        )}
                      >
                        {!disabled ? (
                          <>
                            <small className="text-[13px] font-inter-medium">
                              $
                            </small>

                            <input
                              disabled={false}
                              className="w-full text-[13px] ml-1 font-inter-medium focus:outline-none h-[40px] text-black"
                              type="number"
                              placeholder="0.00"
                              value={dailySubsidyOverride[day]}
                              onChange={(e) => {
                                if (e.target.value > -1) {
                                  onChangeDaily(e.target.value, day);
                                  disableButton(false);
                                }
                              }}
                              onWheelCapture={(e) => {
                                e.target.blur();
                              }}
                              onKeyPress={preventMinus}
                            />
                          </>
                        ) : (
                          <div className="w-full h-[40px]"></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {mealAllowanceType !== "" && mealAllowanceType == "weekly" && (
          <div className="screen-lg:w-[541px] screen-md:w-[541px] w-full  px-2 py-4 bg-slate-50 rounded-lg flex-col justify-start items-start gap-2.5 inline-flex">
            <div className="text-slate-700 text-lg font-inter-semibold leading-tight">
              Weekly Allowance Per Person
            </div>

            <div className="hidden screen-lg:flex screen-md:flex screen-sm:hidden w-full screen-lg:w-[512px] screen-md:w-[512px] justify-between items-center gap-6 inline-flex">
              <div className="text-slate-700 text-base font-inter-normal leading-7">
                $0
              </div>

              <Rangeslider
                minValue={0}
                maxValue={mealAllowanceType === "daily" ? "50" : "200"}
                currentValue={mealAllowanceValue}
                setMealAllowance={handleMealAllowance}
              />

              <div className="text-slate-700 text-base font-inter-normal leading-7">
                {mealAllowanceType === "daily" ? "$50" : "$200"}
              </div>
            </div>

            <div className="screen-lg:w-[512px] screen-md:w-[512px] w-full flex-col gap-1 flex">
              <div className="h-[46px] rounded-xl mb-2 bg-white">
                <span className="ml-4 text-slate-700 font-inter-medium">$</span>

                <input
                  type="text"
                  id="otherMeal"
                  value={mealAllowanceValue}
                  placeholder="0.00"
                  min={0}
                  max={mealAllowanceType?.toLowerCase() === "daily" ? 50 : 200}
                  onChange={(e) => {
                    const value = e.target.value;
                    const regex = /^[0-9]*\.?[0-9]*$/;
                    if (regex.test(value)) {
                      const maxValue =
                        mealAllowanceType?.toLowerCase() === "daily" ? 50 : 200;
                      if (value >= 0 && value <= maxValue) {
                        setMealAllowanceValue(value);
                      }
                    }
                  }}
                  className="h-[42px] w-1/2 pl-0 p-4 rounded-xl text-base font-inter-medium leading-7 text-slate-700 outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {mealDeliveryOption === "i will set my own dates" && (
          <>
            <div className="expected-bag-text screen-xs:w-full-235 font-inter-semibold">
              Meal Plan Subsidy
            </div>

            <div className="text-slate-500 font-inter-normal text-base w-[654px]">
              Setting a per meal subsidy allows users to use this amount for
              each meal. You can also assign custom subsidies to individual
              users, but for now, start by setting the default per meal subsidy.
              This subsidy will apply to all current and future meals created
              for this meal plan.
            </div>

            <div className="screen-lg:w-[541px] screen-md:w-[541px] w-full  px-2 py-4 bg-slate-50 rounded-lg flex-col justify-start items-start gap-2.5 inline-flex">
              <div className="hidden screen-lg:flex screen-md:flex screen-sm:hidden w-full screen-lg:w-[512px] screen-md:w-[512px] justify-between items-center gap-6 inline-flex">
                <div className="text-slate-700 text-base font-inter-normal leading-7">
                  $0
                </div>

                <Rangeslider
                  minValue={0}
                  maxValue={200}
                  currentValue={defaultSubsidy}
                  setMealAllowance={setDefaultSubsidy}
                />

                <div className="text-slate-700 text-base font-inter-normal leading-7">
                  $200
                </div>
              </div>

              <div className="screen-lg:w-[512px] screen-md:w-[512px] w-full flex-col gap-1 flex">
                <div className="h-[46px] rounded-xl mb-2 bg-white">
                  <span className="ml-4 text-slate-700 font-inter-medium">
                    $
                  </span>

                  <input
                    type="text"
                    id="otherMeal"
                    value={defaultSubsidy}
                    placeholder="0.00"
                    min={0}
                    max={200}
                    onChange={(e) => {
                      const value = e.target.value;
                      const regex = /^[0-9]*\.?[0-9]*$/;
                      if (regex.test(value)) {
                        if (value >= 0 && value <= 200)
                          setDefaultSubsidy(value);
                      }
                    }}
                    className="h-[42px] w-1/2 pl-0 p-4 rounded-xl text-base font-inter-medium leading-7 text-slate-700 outline-none"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flex-col justify-start items-start gap-[7px] mt-[24px] flex">
          <label className="relative inline-flex items-center cursor-pointer gap-2.5">
            <input
              type="checkbox"
              checked={overPayment}
              className="sr-only peer"
              onChange={(e) => setEnableOverPayment(e.target.checked)}
            />

            <div className=" w-9 h-5 p-0.5 bg-gray-200 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:start-[2px] after:bg-white after:w-4 after:h-4 after:shadow after:border after:rounded-full after:transition-all  peer-checked:bg-indigo-500"></div>

            <span className="text-slate-700 text-sm font-inter-medium leading-none">
              Overpayment
            </span>
          </label>

          <div className="justify-start items-start gap-1 mt-[14px] inline-flex">
            <img src={InfoButtonGray} className="w-3 h-3" alt="DangerIcon" />

            <div className="w-full text-slate-500 text-xs font-inter-normal leading-none w-[644px]">
              Allow users to use their personal credit card when going over set
              allowance to change your settings, please contact your hungerhub
              account admin.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3;
